import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Badge, Card, Empty, ErrorBox, Kpi, Skeleton } from "@/components/ui";
import type { RPISDueReminders, Task } from "@/types";

export default function RPISDueRemindersPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["rpis-due-reminders"],
    queryFn: () => api.get<RPISDueReminders>("/rpis-manager/due-reminders?limit=50"),
  });
  const close = useMutation({
    mutationFn: (id: string) =>
      api.post(`/tasks/${id}/close`, { resolution_note: "Closed from RPIS due reminders UI" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rpis-due-reminders"] }),
  });
  const canWrite = ["admin", "physician", "coordinator"].includes(user?.role || "");

  if (q.isLoading) return <Card><Skeleton rows={8} /></Card>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-50">Due reminders</h1>
          <div className="text-sm text-slate-400">Read-only reminder summary with task actions where available.</div>
        </div>
        <Link className="btn-ghost" to="/rpis-manager">RPIS Manager</Link>
      </div>

      {q.error ? <ErrorBox error={q.error} /> : null}
      {close.error ? <ErrorBox error={close.error} /> : null}

      {q.data ? (
        <>
          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
            <Kpi label="Open tasks" value={q.data.open_tasks.total} tone="info" />
            <Kpi label="Overdue" value={q.data.overdue_tasks.total} tone={q.data.overdue_tasks.total ? "bad" : "ok"} />
            <Kpi label="Due today" value={q.data.due_today.total} tone={q.data.due_today.total ? "warn" : "ok"} />
            <Kpi label="Due this week" value={q.data.due_this_week.total} tone="info" />
          </div>

          <div className="grid xl:grid-cols-3 gap-4">
            <ReminderGroup
              title="Overdue tasks"
              tasks={q.data.overdue_tasks.items}
              canWrite={canWrite}
              onClose={(id) => close.mutate(id)}
            />
            <ReminderGroup
              title="Due today"
              tasks={q.data.due_today.items}
              canWrite={canWrite}
              onClose={(id) => close.mutate(id)}
            />
            <ReminderGroup
              title="Due this week"
              tasks={q.data.due_this_week.items}
              canWrite={canWrite}
              onClose={(id) => close.mutate(id)}
            />
          </div>
        </>
      ) : null}
    </div>
  );
}

function ReminderGroup({
  title,
  tasks,
  canWrite,
  onClose,
}: {
  title: string;
  tasks: Task[];
  canWrite: boolean;
  onClose: (id: string) => void;
}) {
  return (
    <Card title={title}>
      {!tasks.length ? <Empty title="No tasks." /> : (
        <ul className="space-y-2 text-sm">
          {tasks.map((task) => (
            <li key={task.id} className="card-tight">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-medium text-slate-100">{task.title}</div>
                  <div className="mt-1 text-xs text-slate-500">
                    due {task.due_date || "unscheduled"} · patient {task.patient_id?.slice(0, 8) || "unassigned"}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge tone={task.priority === "urgent" ? "bad" : task.priority === "high" ? "warn" : "muted"}>{task.priority}</Badge>
                  <Badge tone={task.status === "closed" ? "ok" : "info"}>{task.status}</Badge>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {task.patient_id ? <Link className="btn-ghost" to={`/patients/${task.patient_id}`}>Open task details</Link> : null}
                {canWrite && task.status !== "closed" && task.status !== "cancelled" ? (
                  <button className="btn-ghost" onClick={() => onClose(task.id)}>Close task</button>
                ) : null}
                {canWrite && task.patient_id ? (
                  <Link className="btn-ghost" to={`/tasks?patient_id=${task.patient_id}`}>Create follow-up task</Link>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
